/**
 * Données d'observation (SPEC.md §5).
 *
 * Un dataset est ce qui départage les modèles. Il n'est donc pas une
 * commodité : c'est la pièce dont dépend la validité de tout le reste. D'où
 * deux partis pris.
 *
 * **`isExternal` n'est pas un champ qu'on renseigne, c'est une conséquence.**
 * Le chargeur le déduit de la complétude de la provenance — source, licence,
 * méthode d'acquisition. Du code qui *calcule* des valeurs ne peut pas
 * honnêtement déclarer par quelle méthode il les a mesurées ni sous quelle
 * licence il les diffuse. Rien n'empêche de forger une fausse provenance, mais
 * il faut alors mentir dans la donnée, délibérément — pas se tromper de champ.
 *
 * **Le chargeur rassemble tous les reproches.** Un CSV avec quatre erreurs les
 * signale toutes, avec leur numéro de ligne. Abandonner au premier problème
 * transforme la correction d'un fichier en jeu de devinettes.
 */

import { coreFr } from "../i18n/core-fr.js";
import { DATASET_LIMITS } from "../data/limits.js";
import { fr } from "../i18n/fr.js";
import { julianDayFromISO } from "./time.js";
import type { Quantity, Unit } from "./units.js";
import { dimensionOf, isUnit, quantity, toCanonical } from "./units.js";

export interface Problem {
  /**
   * Numéro de ligne dans le fichier, 1-indexé. 0 pour un reproche global.
   *
   * Exception assumée à `no-bare-number` : une position dans un fichier n'est
   * pas une grandeur physique, elle n'a pas d'unité à porter. La règle ne peut
   * pas faire cette distinction ; l'exception est donc explicite et visible en
   * revue, ce qui est exactement ce qu'on attend d'elle.
   */
  // eslint-disable-next-line verlet/no-bare-number
  readonly line: number;
  readonly message: string;
}

export type Cell =
  | { readonly kind: "text"; readonly value: string }
  | {
      readonly kind: "quantity";
      readonly value: Quantity;
      readonly uncertainty?: Quantity;
    };

export type Row = Readonly<Record<string, Cell>>;

export interface Column {
  readonly name: string;
  /** Absente pour une colonne de texte. */
  readonly unit?: Unit;
  /** Vraie si une colonne `<nom>_uncertainty` accompagne celle-ci. */
  readonly hasUncertainty: boolean;
}

export interface Dataset {
  readonly name: string;
  readonly source: string;
  readonly license: string;
  readonly acquisitionMethod: string;
  readonly publishedOn: string;
  /** Déduit de la provenance, jamais écrit par le code de calcul. */
  readonly isExternal: boolean;
  readonly columns: readonly Column[];
  readonly rows: readonly Row[];
}

export type DatasetLoad =
  | { readonly ok: true; readonly dataset: Dataset }
  | { readonly ok: false; readonly problems: readonly Problem[] };

const UNCERTAINTY_SUFFIX = "_uncertainty";
const HEADER_PATTERN = /^([A-Za-z][A-Za-z0-9_]*)(?:\[([^\]]+)\])?$/;
const REQUIRED_PROVENANCE = [
  "name",
  "source",
  "license",
  "acquisitionMethod",
  "publishedOn",
] as const;

interface ParsedHeader {
  readonly name: string;
  readonly unit: Unit | undefined;
  readonly measures: string | undefined;
}

/** Une valeur « qui ressemble à un nombre » doit déclarer son unité. */
function looksNumeric(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === "") return false;
  return Number.isFinite(Number(trimmed));
}

function parseMetadata(
  lines: readonly string[],
): { meta: Map<string, string>; firstDataLine: number } {
  const meta = new Map<string, string>();
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (line === undefined) break;
    const trimmed = line.trim();
    if (trimmed === "") {
      index += 1;
      continue;
    }
    if (!trimmed.startsWith("#")) break;

    const body = trimmed.slice(1).trim();
    const separator = body.indexOf(":");
    if (separator > 0) {
      meta.set(body.slice(0, separator).trim(), body.slice(separator + 1).trim());
    }
    index += 1;
  }

  return { meta, firstDataLine: index };
}

function parseHeaderCell(
  raw: string,
  lineNumber: number,
): { readonly header: ParsedHeader | undefined; readonly problems: readonly Problem[] } {
  const match = HEADER_PATTERN.exec(raw.trim());
  if (match === null) {
    return {
      header: undefined,
      problems: [
        {
          line: lineNumber,
          message: coreFr.dataset.invalidHeader(String(raw.trim())),
        },
      ],
    };
  }

  const [, name, rawUnit] = match;
  if (name === undefined) return { header: undefined, problems: [] };

  let unit: Unit | undefined;
  if (rawUnit !== undefined) {
    if (!isUnit(rawUnit)) {
      return {
        header: undefined,
        problems: [
          {
            line: lineNumber,
            message: coreFr.dataset.unknownUnit(String(name), String(rawUnit)),
          },
        ],
      };
    }
    unit = rawUnit;
  }

  const measures = name.endsWith(UNCERTAINTY_SUFFIX)
    ? name.slice(0, -UNCERTAINTY_SUFFIX.length)
    : undefined;

  return { header: { name, unit, measures }, problems: [] };
}

function splitRow(line: string): readonly string[] {
  return line.split(",").map((cell) => cell.trim());
}

function provenanceProblems(meta: ReadonlyMap<string, string>, isFixture: boolean): Problem[] {
  if (isFixture) return [];
  return REQUIRED_PROVENANCE.flatMap((key) =>
    !meta.has(key) || meta.get(key) === ""
      ? [
          {
            line: 0,
            message: coreFr.dataset.missingProvenance(String(key)),
          },
        ]
      : [],
  );
}

interface HeaderSchema {
  readonly headers: readonly ParsedHeader[];
  readonly byName: ReadonlyMap<string, ParsedHeader>;
  readonly problems: readonly Problem[];
}

function parseHeaders(line: string, lineNumber: number): HeaderSchema {
  const headers: ParsedHeader[] = [];
  const problems: Problem[] = [];
  for (const cell of splitRow(line)) {
    const loaded = parseHeaderCell(cell, lineNumber);
    problems.push(...loaded.problems);
    if (loaded.header !== undefined) headers.push(loaded.header);
  }

  const byName = new Map<string, ParsedHeader>();
  for (const header of headers) {
    if (byName.has(header.name)) problems.push({ line: lineNumber, message: fr.datasetDuplicateColumn(header.name) });
    byName.set(header.name, header);
  }
  problems.push(...uncertaintyHeaderProblems(headers, byName, lineNumber));
  return { headers, byName, problems };
}

function uncertaintyHeaderProblems(
  headers: readonly ParsedHeader[],
  byName: ReadonlyMap<string, ParsedHeader>,
  lineNumber: number,
): Problem[] {
  const problems: Problem[] = [];
  for (const header of headers) {
    if (header.measures === undefined) continue;
    const target = byName.get(header.measures);
    if (target === undefined) {
      problems.push({
        line: lineNumber,
        message: coreFr.dataset.missingUncertaintyTarget(String(header.name), String(header.measures)),
      });
      continue;
    }
    if (header.unit === undefined || target.unit === undefined) {
      problems.push({
        line: lineNumber,
        message: coreFr.dataset.uncertaintyUnitsRequired(header.name, header.measures),
      });
      continue;
    }
    if (dimensionOf(header.unit) !== dimensionOf(target.unit)) {
      problems.push({
        line: lineNumber,
        message: coreFr.dataset.incompatibleUncertainty(header.name, header.unit, header.measures, target.unit),
      });
    }
  }
  return problems;
}

interface RowLoad {
  readonly row: Row | undefined;
  readonly problems: readonly Problem[];
}

function readRawCells(
  cells: readonly string[],
  headers: readonly ParsedHeader[],
  lineNumber: number,
): { readonly raw: ReadonlyMap<string, string>; readonly problems: readonly Problem[] } {
  const raw = new Map<string, string>();
  const problems: Problem[] = [];
  for (let column = 0; column < headers.length; column += 1) {
    const header = headers[column];
    const value = cells[column];
    if (header === undefined || value === undefined) continue;
    raw.set(header.name, value);
    if (header.unit !== undefined && looksNumeric(value)) {
      try {
        const canonical = toCanonical(quantity(Number(value), header.unit));
        if (header.measures !== undefined && !(canonical.value > 0)) {
          problems.push({ line: lineNumber, message: fr.datasetPositiveUncertainty(header.name) });
        }
      } catch (error) {
        problems.push({ line: lineNumber, message: fr.datasetInvalidCell(header.name, error instanceof Error ? error.message : String(error)) });
      }
    }
    if (header.name === "utc" && header.unit === undefined) {
      try { julianDayFromISO(value); }
      catch (error) {
        problems.push({ line: lineNumber, message: fr.datasetInvalidCell(header.name, error instanceof Error ? error.message : String(error)) });
      }
    }

    if (header.unit !== undefined && !looksNumeric(value)) {
      problems.push({
        line: lineNumber,
        message: coreFr.dataset.notNumeric(String(header.name), String(value)),
      });
    }
    if (header.unit === undefined && looksNumeric(value)) {
      problems.push({
        line: lineNumber,
        message: coreFr.dataset.missingUnit(String(header.name)),
      });
    }
  }
  return { raw, problems };
}

function rowOf(
  raw: ReadonlyMap<string, string>,
  headers: readonly ParsedHeader[],
  byName: ReadonlyMap<string, ParsedHeader>,
): Row {
  const row: Record<string, Cell> = {};
  for (const header of headers) {
    if (header.measures !== undefined) continue;
    const value = raw.get(header.name);
    if (value === undefined) continue;

    if (header.unit === undefined) {
      row[header.name] = { kind: "text", value };
      continue;
    }

    const canonical = toCanonical(quantity(Number(value), header.unit));
    const uncertaintyRaw = raw.get(`${header.name}${UNCERTAINTY_SUFFIX}`);
    const uncertaintyHeader = byName.get(`${header.name}${UNCERTAINTY_SUFFIX}`);
    row[header.name] =
      uncertaintyRaw !== undefined && uncertaintyHeader?.unit !== undefined
        ? {
            kind: "quantity",
            value: canonical,
            uncertainty: toCanonical(quantity(Number(uncertaintyRaw), uncertaintyHeader.unit)),
          }
        : { kind: "quantity", value: canonical };
  }
  return row;
}

function parseDataRow(
  line: string,
  lineNumber: number,
  headers: readonly ParsedHeader[],
  byName: ReadonlyMap<string, ParsedHeader>,
): RowLoad {
  const cells = splitRow(line);
  if (cells.length !== headers.length) {
    return {
      row: undefined,
      problems: [
        {
          line: lineNumber,
          message: coreFr.dataset.columnCount(String(cells.length), String(headers.length)),
        },
      ],
    };
  }

  const loaded = readRawCells(cells, headers, lineNumber);
  if (loaded.problems.length > 0) return { row: undefined, problems: loaded.problems };
  return { row: rowOf(loaded.raw, headers, byName), problems: [] };
}

interface RowsLoad {
  readonly rows: readonly Row[];
  readonly sawDataLine: boolean;
  readonly problems: readonly Problem[];
}

function parseRows(
  lines: readonly string[],
  firstRowIndex: number,
  headers: readonly ParsedHeader[],
  byName: ReadonlyMap<string, ParsedHeader>,
): RowsLoad {
  const rows: Row[] = [];
  const problems: Problem[] = [];
  let sawDataLine = false;
  let dataLines = 0;

  for (let index = firstRowIndex; index < lines.length; index += 1) {
    const line = lines[index];
    if (line === undefined || line.trim() === "" || line.trim().startsWith("#")) continue;
    sawDataLine = true;
    dataLines += 1;
    if (dataLines > DATASET_LIMITS.rows) {
      problems.push({ line: index + 1, message: fr.datasetTooManyRows(DATASET_LIMITS.rows) });
      break;
    }
    const loaded = parseDataRow(line, index + 1, headers, byName);
    problems.push(...loaded.problems);
    if (loaded.row !== undefined) rows.push(loaded.row);
  }
  return { rows, sawDataLine, problems };
}

function columnsOf(
  headers: readonly ParsedHeader[],
  byName: ReadonlyMap<string, ParsedHeader>,
): readonly Column[] {
  return headers
    .filter((header) => header.measures === undefined)
    .map((header) => {
      const hasUncertainty = byName.has(`${header.name}${UNCERTAINTY_SUFFIX}`);
      return header.unit === undefined
        ? { name: header.name, hasUncertainty }
        : { name: header.name, unit: header.unit, hasUncertainty };
    });
}

export function parseDataset(text: string): DatasetLoad {
  if (text.length > DATASET_LIMITS.bytes || new TextEncoder().encode(text).byteLength > DATASET_LIMITS.bytes) {
    return { ok: false, problems: [{ line: 0, message: fr.datasetTooLarge }] };
  }
  const lines = text.split(/\r?\n/);
  const { meta, firstDataLine } = parseMetadata(lines);

  // Une fixture est un jeu de mise au point : elle se charge sans provenance,
  // et le comparateur la refusera toujours (voir comparability.ts). C'est ce
  // qui permet de développer sans jamais fabriquer de fausse observation.
  const isFixture = meta.get("fixture") === "true";
  const problems = provenanceProblems(meta, isFixture);

  const headerLine = lines[firstDataLine];
  if (headerLine === undefined || headerLine.trim() === "") {
    problems.push({ line: firstDataLine + 1, message: coreFr.dataset.missingHeader });
    return { ok: false, problems };
  }

  const headerNumber = firstDataLine + 1;
  if (splitRow(headerLine).length > DATASET_LIMITS.columns) {
    return { ok: false, problems: [{ line: headerNumber, message: fr.datasetTooManyColumns(DATASET_LIMITS.columns) }] };
  }
  const schema = parseHeaders(headerLine, headerNumber);
  problems.push(...schema.problems);
  if (schema.problems.length > 0) return { ok: false, problems };
  const loadedRows = parseRows(
    lines,
    firstDataLine + 1,
    schema.headers,
    schema.byName,
  );
  problems.push(...loadedRows.problems);

  if (!loadedRows.sawDataLine) {
    problems.push({
      line: 0,
      message: coreFr.dataset.emptyDataset,
    });
  }

  if (problems.length > 0) return { ok: false, problems };

  return {
    ok: true,
    dataset: {
      name: meta.get("name") ?? "",
      source: meta.get("source") ?? "",
      license: meta.get("license") ?? "",
      acquisitionMethod: meta.get("acquisitionMethod") ?? "",
      publishedOn: meta.get("publishedOn") ?? "",
      isExternal: !isFixture,
      columns: columnsOf(schema.headers, schema.byName),
      rows: loadedRows.rows,
    },
  };
}

/** Colonne du dataset, si elle existe. */
export function columnOf(dataset: Dataset, name: string): Column | undefined {
  return dataset.columns.find((column) => column.name === name);
}

/** Instant d'une ligne, converti depuis sa colonne ISO 8601 UTC. */
export function instantOf(row: Row, column: string): Quantity<"time"> | undefined {
  const cell = row[column];
  if (cell === undefined || cell.kind !== "text") return undefined;
  try { return julianDayFromISO(cell.value); }
  catch { return undefined; }
}
